import type { ComponentPropsWithoutRef, PropsWithoutRef, RefAttributes } from 'react';
import React from 'react';
import { styled, css } from 'styled-components';

import BarsLoader, { Bar } from '../../Elements/BarsLoader/index.tsx';
import { Media } from '../../theme/Media.tsx';

export type ButtonFC<T, P = Record<string, never>> = React.ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

const ButtonLoader = styled(BarsLoader)<{ $square?: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  width: ${({ $square }) => ($square ? '24px' : '32px')};
  height: 18px;
`;

type NativeButtonProps = Omit<ComponentPropsWithoutRef<'button'>, 'as'>;

export type ButtonProps = NativeButtonProps & {
  variant?: 'create' | 'create-secondary' | 'accept' | 'edit' | 'delete';
  isLoading?: boolean;
  inline?: boolean;
  square?: boolean;
  icon?: boolean;
  detailAction?: boolean;
  as?: React.ElementType;
  to?: string;
  href?: string;
  target?: string;
  rel?: string;
  dataTestId?: string;
  pseudoDisabled?: boolean;
};

export interface IButtonContainer {
  $styled: Pick<ButtonProps, 'variant' | 'square' | 'icon' | 'detailAction' | 'isLoading' | 'pseudoDisabled'>;
}

export const buttonStyles = css<IButtonContainer>`
  position: relative;
  font-size: 12px;
  letter-spacing: 0.1px;
  line-height: 14px;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.grey00_2}!important;
  padding: ${({ $styled: { square } }) => !square && '0 35px'};
  width: ${({ $styled: { square, icon, detailAction } }) => (square || icon || detailAction ? '36px' : '100%')};
  height: 36px;
  border-radius: 4px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border: none;
  outline: none;
  cursor: pointer;
  transition:
    box-shadow 0.3s,
    background-color 0.3s,
    color 0.3s;

  &:not([disabled]) {
    ${({ $styled: { variant }, theme }) =>
      variant === 'create' &&
      css`
        background-color: ${theme.colors.main};

        &:hover {
          background-color: ${theme.colors.mainDark};
        }
      `}
    ${({ $styled: { variant }, theme }) =>
      variant === 'accept' &&
      css`
        background-color: ${theme.colors.positive100};

        &:hover {
          background-color: ${theme.colors.positive120};
        }
      `}
    ${({ $styled: { variant }, theme }) =>
      variant === 'edit' &&
      css`
        background-color: ${theme.colors.link100};

        &:hover {
          background-color: ${theme.colors.link120};
        }
      `}
    ${({ $styled: { variant }, theme }) =>
      variant === 'delete' &&
      css`
        background-color: ${theme.colors.critical100};

        &:hover {
          background-color: ${theme.colors.critical120};
        }
      `}
    ${({ $styled: { variant, square }, theme }) =>
      variant === 'create-secondary' &&
      !square &&
      css`
        background-color: transparent;
        border: 2px solid ${theme.colors.main};
        color: ${theme.colors.black_1}!important;

        &:hover {
          background-color: ${theme.colors.main};
          border-color: ${theme.colors.main};
          color: ${theme.colors.grey00_2}!important;
        }
        ${Bar} {
          background-color: ${theme.colors.black_1};
        }
      `}
    ${({ $styled: { variant, square }, theme }) =>
      variant === 'create-secondary' &&
      square &&
      css`
        background-color: ${theme.colors.link100};

        &:hover {
          background-color: ${theme.colors.link120};
        }
      `}
  }

  &[disabled] {
    background-color: ${({ theme }) => theme.colors.grey70_1};
    cursor: not-allowed;

    &:hover {
      background-color: ${({ theme }) => theme.colors.grey70_1};
    }
    ${({ $styled: { variant, square }, theme }) =>
      variant === 'create-secondary' &&
      !square &&
      css`
        border: 2px solid ${theme.colors.grey70_1};
      `}
  }
  ${({ $styled: { icon, pseudoDisabled, variant, square }, theme }) =>
    !icon &&
    css`
      ${pseudoDisabled &&
      css`
        background-color: ${theme.colors.grey70_1}!important;
        cursor: not-allowed;
        pointer-events: none;
        box-shadow: none !important;
        border: ${variant === 'create-secondary' && !square && `2px solid ${theme.colors.grey70_1}`};
      `}
    `};
  ${({ $styled: { icon, detailAction, isLoading, pseudoDisabled }, theme }) =>
    (icon || detailAction) &&
    css`
      min-width: 36px;
      padding: 0;
      font-size: ${!detailAction && '18px'};
      background: transparent;
      transition: opacity 0.3s;

      &,
      &:visited,
      &:hover {
        color: ${theme.colors.black_1}!important;

        &[disabled] {
          color: ${theme.colors.black_1}!important;
          background-color: transparent;
          box-shadow: none;
          opacity: ${() => !isLoading && 0.3};
        }
        ${pseudoDisabled &&
        css`
          color: ${theme.colors.black_1}!important;
          background-color: transparent;
          box-shadow: none;
          opacity: ${() => !isLoading && 0.3};
          pointer-events: none;
        `}
      }
      ${Bar} {
        background-color: ${theme.colors.black_1};
      }

      @media ${Media.mdMax} {
        width: 100%;
      }
    `};
`;

const ButtonContainer = styled.button<IButtonContainer>`
  ${buttonStyles}
`;

export interface IContainer {
  $styled: Pick<ButtonProps, 'square' | 'inline' | 'pseudoDisabled'>;
}

export const Container = styled.div<IContainer>`
  position: relative;
  display: ${({ $styled: { square, inline } }) => (square || inline ? 'inline-flex' : 'flex')};
  cursor: ${({ $styled: { pseudoDisabled } }) => pseudoDisabled && 'not-allowed'};
`;

const ButtonContent = styled.div<{ $styled: Pick<ButtonProps, 'isLoading'> }>`
  opacity: ${({ $styled: { isLoading } }) => isLoading && 0};
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const Button: ButtonFC<HTMLButtonElement, ButtonProps> = React.forwardRef(function Button(
  {
    isLoading = false,
    className = '',
    onClick = undefined,
    inline = false,
    children,
    square,
    icon,
    detailAction,
    variant = !icon && !detailAction && 'create',
    dataTestId,

    pseudoDisabled,
    ...other
  },
  ref,
) {
  return (
    <Container
      $styled={{
        square,
        inline,
        pseudoDisabled,
      }}>
      <ButtonContainer
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => !isLoading && onClick && onClick(e)}
        className={className}
        type='button'
        ref={ref}
        $styled={{
          variant,
          square,
          icon,
          detailAction,
          isLoading,
          pseudoDisabled,
        }}
        data-testid={dataTestId}
        {...other}>
        <ButtonContent $styled={{ isLoading }}>{children}</ButtonContent>
        {isLoading && <ButtonLoader $square={square} />}
      </ButtonContainer>
    </Container>
  );
});

export type ButtonWithAddons = ButtonFC<HTMLButtonElement, ButtonProps> & {
  Container: typeof Container;
};

(Button as ButtonWithAddons).Container = Container;

export default Button as ButtonWithAddons;
